import { useState, useEffect, useContext } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import axios from "axios";
import { AuthContext } from "../../Context/AuthContext";
import Preloader from "../../shared/PreLoaders";
import { Bar } from "react-chartjs-2";
import { logActivity } from "../../utils/LogActivity";
import { Authorization } from "../../Context/Authorization";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/* ── Shared styles ─────────────────────────────────────────── */
const selectStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", cursor: "pointer", transition: "border-color 0.2s",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };

const FilterSelect = ({ label, value, onChange, children }) => (
  <div style={{ marginBottom: 0 }}>
    <label style={labelStyle}>{label}</label>
    <select
      style={selectStyle}
      value={value}
      onChange={onChange}
      onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
      onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
    >
      {children}
    </select>
  </div>
);

/* ── Info row inside profile ───────────────────────────────── */
const InfoRow = ({ icon, label, value }) =>
  value ? (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: "1px solid #F7FAFC" }}>
      <span style={{ width: 28, height: 28, background: "#E8F5E9", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <i className={`mdi ${icon}`} style={{ fontSize: 13, color: "#2E7D32" }} />
      </span>
      <div>
        <div style={{ fontSize: 10, color: "#A0AEC0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
        <div style={{ fontSize: 13, color: "#2D3748", fontWeight: 500, marginTop: 1 }}>{value}</div>
      </div>
    </div>
  ) : null;

/* ── Detail grid cell ──────────────────────────────────────── */
const DetailCell = ({ label, value }) => (
  <div style={{ background: "#F7FAFC", borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
    <div style={{ fontSize: 10, color: "#A0AEC0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 13, color: "#2D3748", fontWeight: 600 }}>{value || <span style={{ color: "#CBD5E0" }}>—</span>}</div>
  </div>
);

/* ── MemberProfile ──────────────────────────────────────────── */
const MemberProfile = () => {
  const { userId, selectedJfmcType } = useContext(AuthContext);
  const Api = process.env.REACT_APP_API;

  const [ranges, setRanges]               = useState([]);
  const [vfcData, setVfcData]             = useState([]);
  const [memberData, setMemberData]       = useState([]);
  const [selectedRange, setSelectedRange] = useState("");
  const [selectedVfc, setSelectedVfc]     = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberToShow, setMemberToShow]   = useState(null);
  const [loading, setLoading]             = useState(true);

  /* ── Fetch ranges on mount ── */
  useEffect(() => {
    const fetchRanges = async () => {
      setLoading(true);
      try {
        const config = Authorization();
        const res = await axios.get(`${Api}/api/ranges`, config);
        setRanges(res.data);
      } catch (err) {
        console.error("Error fetching ranges", err);
      } finally {
        setLoading(false);
      }
    };
    fetchRanges();
  }, [Api]);

  /* ── Fetch VFCs when selectedJfmcType changes ── */
  useEffect(() => {
    if (!selectedJfmcType) {
      setVfcData([]);
      setMemberData([]);
      setSelectedRange("");
      setSelectedVfc("");
      setSelectedMember(null);
      setMemberToShow(null);
    }
  }, [selectedJfmcType]);

  const handleRangeChange = async (e) => {
    const rangeId = e.target.value;
    setSelectedRange(rangeId);
    setSelectedVfc("");
    setMemberData([]);
    setSelectedMember(null);
    setMemberToShow(null);
    if (!rangeId) { setVfcData([]); return; }
    setLoading(true);
    try {
      const config = Authorization();
      const res = await axios.post(
        `${Api}/api/vfcs/vfcs-by-range`,
        { rangeId, jfmcTypeId: selectedJfmcType?._id },
        config
      );
      setVfcData(Array.isArray(res.data.vfcs) ? res.data.vfcs : []);
    } catch (err) {
      console.error("Error fetching VFCs by range", err);
      setVfcData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleVfcChange = async (e) => {
    const vfcId = e.target.value;
    setSelectedVfc(vfcId);
    setSelectedMember(null);
    setMemberToShow(null);
    if (!vfcId) { setMemberData([]); return; }
    setLoading(true);
    try {
      const config = Authorization();
      const res = await axios.get(`${Api}/api/members/getMembersByVFC/${vfcId}`, config);
      setMemberData(res.data.members || []);
    } catch (err) {
      console.error("Error fetching members", err);
      setMemberData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMemberChange = (e) => {
    const member = memberData.find((m) => m._id === e.target.value) || null;
    setSelectedMember(member);
    setMemberToShow(null);
  };

  const handleViewClick = async () => {
    if (!selectedMember) return;
    setLoading(true);
    try {
      const config = Authorization();
      const res = await axios.get(
        `${Api}/api/members/getMemberLoanDetails/${selectedMember._id}`,
        config
      );
      const { member, loans } = res.data;
      setMemberToShow({ ...member, loans });
      await logActivity(userId, `Viewed Loan Details for ${member.name}`, "MemberProfile Page", Api);
    } catch (err) {
      console.error("Error fetching member loan details", err);
    } finally {
      setLoading(false);
    }
  };

  const generateChartData = (repaymentHistory = []) => ({
    labels: repaymentHistory.map((item) => item.month),
    datasets: [
      {
        label: "Repayment Amount",
        data: repaymentHistory.map((item) => item.amount),
        backgroundColor: "rgba(46, 125, 50, 0.6)",
        borderColor: "#2E7D32",
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  });

  /* ── Initials avatar helper ── */
  const getInitials = (name = "") =>
    name.split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("");

  /* ════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════ */
  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Member Profile</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>Member</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>Profile View</span>
        </div>
      </div>

      {/* ── No JFMC type warning ── */}
      {!selectedJfmcType && (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "32px 24px", textAlign: "center", marginBottom: 20 }}>
          <div style={{ width: 52, height: 52, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 24, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar to choose a type before viewing member profiles.</div>
        </div>
      )}

      {selectedJfmcType && (
        <>
          {/* ── Filter Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", marginBottom: 24, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: "1px solid #EDF2F7", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
              <span style={{ width: 36, height: 36, background: "rgba(255,255,255,0.15)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18 }}>
                <i className="mdi mdi-filter-outline" />
              </span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Member Selection</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>Select Range, JFMC and Member to view the profile</div>
              </div>
            </div>
            <div style={{ padding: "20px 24px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 20 }}>
                <FilterSelect label="Range" value={selectedRange} onChange={handleRangeChange}>
                  <option value="">Select Range</option>
                  {ranges.map((r) => (
                    <option key={r._id} value={r._id}>{r.rangeName}</option>
                  ))}
                </FilterSelect>

                <FilterSelect label="JFMC Name" value={selectedVfc} onChange={handleVfcChange}>
                  <option value="">Select JFMC</option>
                  {vfcData.map((v) => (
                    <option key={v._id} value={v._id}>{v.vfcName}</option>
                  ))}
                </FilterSelect>

                <FilterSelect label="Member" value={selectedMember?._id || ""} onChange={handleMemberChange}>
                  <option value="">Select Member</option>
                  {memberData.map((m) => (
                    <option key={m._id} value={m._id}>{m.name}</option>
                  ))}
                </FilterSelect>
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="button"
                  onClick={handleViewClick}
                  disabled={!selectedMember}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "10px 24px", background: selectedMember ? "linear-gradient(135deg,#1B5E20,#2E7D32)" : "#E2E8F0",
                    color: selectedMember ? "#fff" : "#A0AEC0", border: "none", borderRadius: 10,
                    fontSize: 13, fontWeight: 700, cursor: selectedMember ? "pointer" : "not-allowed",
                    boxShadow: selectedMember ? "0 3px 10px rgba(46,125,50,0.3)" : "none", transition: "all 0.2s",
                  }}
                >
                  <i className="mdi mdi-eye-outline" />
                  View Profile
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedRange(""); setSelectedVfc(""); setSelectedMember(null);
                    setVfcData([]); setMemberData([]); setMemberToShow(null);
                  }}
                  style={{
                    padding: "10px 20px", background: "#fff", color: "#4A5568",
                    border: "1.5px solid #E2E8F0", borderRadius: 10, fontSize: 13,
                    fontWeight: 600, cursor: "pointer",
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          {/* ── Profile Section ── */}
          {memberToShow && (
            <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>

              {/* Left column */}
              <div style={{ width: "35%", flexShrink: 0 }}>
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden", marginBottom: 16 }}>
                  {/* Avatar / photo area */}
                  <div style={{ background: "linear-gradient(135deg,#1B5E20,#2E7D32)", padding: "28px 20px 20px", textAlign: "center" }}>
                    {memberToShow.profilePictureUrl ? (
                      <img
                        src={memberToShow.profilePictureUrl}
                        alt={memberToShow.name}
                        style={{ width: 90, height: 90, borderRadius: "50%", objectFit: "cover", border: "3px solid rgba(255,255,255,0.4)", marginBottom: 12 }}
                      />
                    ) : (
                      <div style={{ width: 90, height: 90, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", fontSize: 30, color: "#fff", fontWeight: 700, border: "3px solid rgba(255,255,255,0.3)" }}>
                        {getInitials(memberToShow.name)}
                      </div>
                    )}
                    <div style={{ color: "#fff", fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{memberToShow.name}</div>
                    <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 12, marginBottom: 8 }}>{memberToShow.vfcCode?.vfcName || "—"}</div>
                    {memberToShow.work && (
                      <span style={{ display: "inline-block", background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 11, fontWeight: 600, padding: "3px 12px", borderRadius: "50rem", border: "1px solid rgba(255,255,255,0.3)" }}>
                        {memberToShow.work}
                      </span>
                    )}
                  </div>

                  {/* Info rows */}
                  <div style={{ padding: "16px 20px" }}>
                    <InfoRow icon="mdi-phone-outline"         label="Phone"         value={memberToShow.phone} />
                    <InfoRow icon="mdi-whatsapp"              label="WhatsApp"      value={memberToShow.whatsAppNo} />
                    <InfoRow icon="mdi-email-outline"         label="Email"         value={memberToShow.eMail} />
                    <InfoRow icon="mdi-calendar-outline"      label="Date of Birth" value={memberToShow.dob ? new Date(memberToShow.dob).toLocaleDateString() : null} />
                    <InfoRow icon="mdi-account-heart-outline" label="Marital Status" value={memberToShow.maritialStatus} />
                    <InfoRow icon="mdi-map-marker-outline"    label="Address"       value={[memberToShow.address, memberToShow.village, memberToShow.panchayath, memberToShow.district].filter(Boolean).join(", ")} />
                  </div>

                  {/* Signature */}
                  {memberToShow.signatureUrl && (
                    <div style={{ padding: "0 20px 20px", textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: "#A0AEC0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>Signature</div>
                      <img src={memberToShow.signatureUrl} alt="Signature" style={{ maxWidth: 120, border: "1px solid #E2E8F0", borderRadius: 6, padding: 6 }} />
                    </div>
                  )}
                </div>
              </div>

              {/* Right column */}
              <div style={{ flex: 1, minWidth: 0 }}>

                {/* Personal details */}
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", marginBottom: 16, overflow: "hidden" }}>
                  <div style={{ padding: "14px 20px", borderBottom: "1px solid #EDF2F7", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 28, height: 28, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13 }}>
                      <i className="mdi mdi-account-outline" />
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Personal Details</span>
                  </div>
                  <div style={{ padding: "16px 20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                    <DetailCell label="Full Name"            value={memberToShow.name} />
                    <DetailCell label="Age"                  value={memberToShow.age} />
                    <DetailCell label="Gender"               value={memberToShow.gender} />
                    <DetailCell label="Community"            value={memberToShow.community} />
                    <DetailCell label="Father / Husband"     value={memberToShow.fatherOrHusband} />
                    <DetailCell label="Father / Husband Name" value={memberToShow.fatherOrHusbandName} />
                    <DetailCell label="Village"              value={memberToShow.village} />
                    <DetailCell label="Panchayath"           value={memberToShow.panchayath} />
                    <DetailCell label="Taluk"                value={memberToShow.thaluk} />
                    <DetailCell label="Pin Code"             value={memberToShow.pinCode} />
                    <DetailCell label="District"             value={memberToShow.district} />
                    <DetailCell label="Occupation"           value={memberToShow.work} />
                    <DetailCell label="Education"            value={memberToShow.educationalQualification} />
                    <DetailCell label="Income"               value={memberToShow.income ? `₹ ${memberToShow.income}` : null} />
                    <DetailCell label="Family Members"       value={memberToShow.familyMemberDetails} />
                  </div>
                </div>

                {/* Bank details */}
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", marginBottom: 16, overflow: "hidden" }}>
                  <div style={{ padding: "14px 20px", borderBottom: "1px solid #EDF2F7", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 28, height: 28, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13 }}>
                      <i className="mdi mdi-bank-outline" />
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Bank Details</span>
                  </div>
                  <div style={{ padding: "16px 20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                    <DetailCell label="Bank Name"      value={memberToShow.bank} />
                    <DetailCell label="Branch"         value={memberToShow.branch} />
                    <DetailCell label="Account Number" value={memberToShow.accountNumber} />
                    <DetailCell label="IFSC Code"      value={memberToShow.ifscCode} />
                  </div>
                </div>

                {/* Identity documents */}
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", marginBottom: 16, overflow: "hidden" }}>
                  <div style={{ padding: "14px 20px", borderBottom: "1px solid #EDF2F7", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 28, height: 28, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13 }}>
                      <i className="mdi mdi-card-account-details-outline" />
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Identity Documents</span>
                  </div>
                  <div style={{ padding: "16px 20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                    <DetailCell label="Aadhar Number"      value={memberToShow.aadhar} />
                    <DetailCell label="Ration Card Number" value={memberToShow.rationCard} />
                  </div>
                </div>

                {/* Loan history */}
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
                  <div style={{ padding: "14px 20px", borderBottom: "1px solid #EDF2F7", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 28, height: 28, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13 }}>
                      <i className="mdi mdi-chart-bar" />
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Loan Repayment History</span>
                  </div>
                  <div style={{ padding: "16px 20px" }}>
                    {memberToShow.loans && memberToShow.loans.length > 0 ? (
                      memberToShow.loans.map((loan, index) => (
                        <div key={index} style={{ marginBottom: 28 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#2D3748", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ display: "inline-block", background: "#E8F5E9", color: "#1B5E20", padding: "2px 10px", borderRadius: "50rem", fontSize: 11, border: "1px solid #A5D6A7" }}>
                              Loan No: {loan.subLoans?.[0]?.subLoanId || `#${index + 1}`}
                            </span>
                          </div>
                          <Bar
                            data={generateChartData(loan.repaymentHistory || [])}
                            options={{
                              responsive: true,
                              plugins: {
                                legend: { position: "top" },
                                title: { display: false },
                              },
                              scales: {
                                y: { beginAtZero: true, grid: { color: "#F7FAFC" } },
                                x: { grid: { display: false } },
                              },
                            }}
                          />
                        </div>
                      ))
                    ) : (
                      <div style={{ textAlign: "center", padding: "32px 0", color: "#A0AEC0" }}>
                        <i className="mdi mdi-chart-bar" style={{ fontSize: 36, marginBottom: 8, display: "block" }} />
                        <div style={{ fontSize: 13, fontWeight: 600 }}>No loan history available</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MemberProfile;
